import {Injectable, NgZone} from '@angular/core';
import {Client, IMessage, StompSubscription} from '@stomp/stompjs';
import {BehaviorSubject, Observable, skip, timer} from 'rxjs';
import {environment} from '../../../environments/environment';
import {UserInfoStorageService} from '../auth/user-info-storage.service';
import {AppConfigService} from "../app-config.service";

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private stompClient: Client | null = null;
  private messageSubjects: { [key: string]: BehaviorSubject<any> } = {};
  private topicSubscriptions: { [key: string]: StompSubscription } = {};
  private messagePublishQueue: { destination: string; payload: any }[] = [];
  private messageSubscribeQueue: { topic: string }[] = [];
  private connectionStatus = new BehaviorSubject<boolean>(false);
  private connecting = false;
  private consecutiveCloseRetries = 0;
  private readonly RECONNECT_BACKOFF = 5000;
  // stompjs retries internally forever on its own (see reconnectDelay below). If the real
  // session is actually gone this backstop kills the Client rather than retrying forever -
  // e.g. the marker cookie outliving the real session (not yet cleared after a server-side
  // invalidation), or any other case where the handshake will just never succeed again.
  private readonly MAX_CONSECUTIVE_CLOSE_RETRIES = 20;

  constructor(
    public authService: UserInfoStorageService,
    private confService: AppConfigService,
    private ngZone: NgZone
  ) {
    this.authService.getTokenObservable().subscribe((token) => {
      if (token && !this.stompClient) {
        this.connect();
      } else if (!token && this.stompClient) {
        this.disconnect();
      }
    });

  }

  private websocketUrl(): string {
    const api = new URL(this.apiUrl(), window.location.href);
    const wsProtocol = (api.protocol === 'https:') ? 'wss:' : 'ws:';
    return `${wsProtocol}//${api.host}/ws`;
  }

  private connect() {
    if (this.connecting || this.stompClient?.active) {
      console.warn('Connection already in progress or active');
      return;
    }

    this.connecting = true;

    // No token in the URL: the session cookie is HttpOnly and is attached to the
    // WS handshake automatically by the browser, same as any other same-origin request.
    this.stompClient = new Client({
      brokerURL: this.websocketUrl(),
      maxWebSocketChunkSize: 128 * 1024,
      reconnectDelay: this.RECONNECT_BACKOFF,
      // Heartbeats fire from a main-thread timer. A long synchronous task (heavy grid
      // render, overlay open) can delay them; a 10s window meant any stall > 10s made
      // the server drop the session. 20s gives the main thread more slack to recover.
      // Must stay in sync with WebSocketConfig#configureMessageBroker on the backend.
      heartbeatIncoming: 20000,
      heartbeatOutgoing: 20000,
    });

    this.stompClient.onConnect = () => {
      this.connecting = false;
      this.connectionStatus.next(true);
      this.consecutiveCloseRetries = 0;

      this.topicSubscriptions = {};
      this.reSubscribeAllTopics();
      this.flushQueue();
    };

    this.stompClient.onStompError = (frame) => {
      // console.error('🔥 STOMP error:', frame, frame.headers, frame.body);
      this.connecting = false;
    };

    this.stompClient.onWebSocketError = (event) => {
      console.error('❌ WebSocket error:', event);
      this.connecting = false;
    };

    this.stompClient.onWebSocketClose = (event: CloseEvent) => {
      this.connecting = false;
      this.connectionStatus.next(false);
      this.topicSubscriptions = {};

      if (event.code === 1009) {
        console.warn('Large message detected. Ensure server buffer is increased or reduce message size.');
      }

      // stompjs's own reconnectDelay (set below) already retries this same Client
      // internally on any close - we must NOT also call connect() here ourselves. Doing so
      // used to spin up a second, independent Client (the old one, still holding its own
      // internal reconnect loop, was simply dropped on the floor instead of deactivated),
      // so logging out - which only deactivates whatever this.stompClient currently points
      // to - left every earlier abandoned Client retrying forever. One Client, one retry
      // loop: we only ever step in to enforce MAX_CONSECUTIVE_CLOSE_RETRIES by killing it.
      this.consecutiveCloseRetries++;
      if (this.consecutiveCloseRetries >= this.MAX_CONSECUTIVE_CLOSE_RETRIES) {
        console.warn(`WebSocket reconnect gave up after ${this.MAX_CONSECUTIVE_CLOSE_RETRIES} consecutive failures.`);
        this.disconnect();
      }
    };

    this.stompClient.activate();
  }

  private handleIncomingMessage(topic: string, message: IMessage) {
    const contentLength = parseInt(message.headers['content-length'] || '0', 10);
    if (contentLength > this.confService.wsMaxMessageSize) {
      console.warn(`Received message too large (${contentLength} bytes) on topic ${topic}. Skipping...`);
      return;
    }

    let body: any;
    try {
      body = JSON.parse(message.body);
    } catch (e) {
      body = message.body;
    }

    if (this.messageSubjects[topic]) {
      this.ngZone.run(() => {
        Promise.resolve().then(() => {
          this.messageSubjects[topic]?.next(body);
        });
      });
    }
  }

  private subscribeInternal(topic: string) {
    if (!this.isConnected() || !this.stompClient || !this.messageSubjects[topic] || this.topicSubscriptions[topic]) {
      return;
    }

    this.topicSubscriptions[topic] = this.stompClient.subscribe(topic, (message: IMessage) => {
      this.handleIncomingMessage(topic, message);
    });
  }

  unsubscribeFromTopic(topic: string) {
    if (this.topicSubscriptions[topic]) {
      this.topicSubscriptions[topic].unsubscribe();
      delete this.topicSubscriptions[topic];
    }

    this.messageSubscribeQueue = this.messageSubscribeQueue.filter(entry => entry.topic !== topic);

    if (this.messageSubjects[topic]) {
      this.messageSubjects[topic].complete();
      delete this.messageSubjects[topic];
    }
  }

  subscribeToTopic<T>(topic: string): Observable<T> {
    if (!this.messageSubjects[topic]) {
      this.messageSubjects[topic] = new BehaviorSubject<T>(undefined as T);

      if (this.isConnected()) {
        this.subscribeInternal(topic);
      } else if (!this.messageSubscribeQueue.some(entry => entry.topic === topic)) {
        this.messageSubscribeQueue.push({topic});
      }
    }

    return this.messageSubjects[topic].asObservable().pipe(skip(1));
  }

  private reSubscribeAllTopics() {
    Object.keys(this.messageSubjects).forEach((topic) => {
      this.subscribeInternal(topic);
    });
  }

  sendMessage(destination: string, payload: any) {
    const payloadString = JSON.stringify(payload);
    if (payloadString.length > this.confService.wsMaxMessageSize) {
      console.warn(`Payload too large (${payloadString.length} bytes) for destination ${destination}`);
      return;
    }

    if (this.isConnected()) {
      this.stompClient?.publish({destination, body: payloadString});
    } else {
      this.messagePublishQueue.push({destination, payload});
      if (!this.connecting && !this.stompClient?.active) {
        this.connect();
      }
    }
  }

  private flushQueue() {
    if (!this.isConnected()) {
      console.warn('Cannot flush queue, not connected. Retrying in 1s...');
      timer(1000).subscribe(() => this.flushQueue());
      return;
    }

    const publishBatch = this.messagePublishQueue.splice(0, 10);
    for (const {destination, payload} of publishBatch) {
      const payloadString = JSON.stringify(payload);
      if (payloadString.length > this.confService.wsMaxMessageSize) {
        console.warn(`Queued payload too large (${payloadString.length} bytes) for destination ${destination}`);
        continue;
      }
      this.stompClient?.publish({destination, body: payloadString});
    }

    const subscribeBatch = this.messageSubscribeQueue.splice(0, 10);
    for (const {topic} of subscribeBatch) {
      this.subscribeInternal(topic);
    }

    if (this.messagePublishQueue.length > 0 || this.messageSubscribeQueue.length > 0) {
      timer(100).subscribe(() => this.flushQueue());
    }
  }

  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus.asObservable();
  }

  protected apiUrl(): string {
    return environment.apiUrl;
  }

  isConnected(): boolean {
    return !!this.stompClient && this.stompClient.connected;
  }

  disconnect() {
    if (this.stompClient) {
      Object.values(this.topicSubscriptions).forEach(subscription => subscription.unsubscribe());
      this.topicSubscriptions = {};
      this.stompClient.deactivate();
      this.stompClient = null;
      this.connectionStatus.next(false);
      this.connecting = false;
      this.consecutiveCloseRetries = 0;
    }
  }
}
